import { cn } from "@/lib/utils";
import { Sentry } from "@/lib/sentry";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Fehler loggen für Debugging
    console.error('[ErrorBoundary] Unerwarteter Fehler:', error, errorInfo);

    // Fehler an Sentry senden
    Sentry.captureException(error, {
      contexts: {
        react: { componentStack: errorInfo.componentStack ?? undefined },
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen p-8 bg-gray-50">
          <div className="flex flex-col items-center w-full max-w-2xl p-8 bg-white rounded-2xl shadow-sm border border-gray-200">
            <AlertTriangle
              size={48}
              className="text-red-500 mb-6 flex-shrink-0"
            />

            <h2 className="text-xl font-semibold text-gray-900 mb-2">Ein unerwarteter Fehler ist aufgetreten</h2>
            <p className="text-sm text-gray-500 mb-6">Bitte laden Sie die Seite neu. Falls das Problem weiterhin besteht, kontaktieren Sie den Support.</p>

            <div className="p-4 w-full rounded-xl bg-gray-50 border border-gray-200 overflow-auto mb-6 max-h-48">
              <pre className="text-xs text-gray-500 whitespace-break-spaces">
                {this.state.error?.message || 'Unbekannter Fehler'}
              </pre>
            </div>

            <button
              onClick={() => window.location.reload()}
              className={cn(
                "flex items-center gap-2 px-5 py-2.5 rounded-xl",
                "bg-[#C41E3A] text-white font-medium",
                "hover:bg-[#A3172E] transition-colors cursor-pointer"
              )}
            >
              <RotateCcw size={16} />
              Seite neu laden
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
